import type { ThemeConfig } from '@chakra-ui/react'
import type { GlobalProps } from '@emotion/react'
import { fonts } from './fonts'

const config: ThemeConfig = {
  initialColorMode: 'light',
  useSystemColorMode: false
}

const breakpoints = {
  xs: '30em',
  sm: '40em',
  md: '52em',
  lg: '64em',
  xl: '80em'
}

/** Orange brand scale — the default colorScheme (buttons, checkboxes, …). */
const customColorScheme = {
  900: '#804000',
  800: '#b35900',
  700: '#ff9933',
  600: '#ffab58',
  500: '#ffb366',
  400: '#ffbf80',
  300: '#ffcc99',
  200: '#ffd9b3',
  100: '#ffe6cc',
  50: '#fff2e6'
}

/** Grey-blue scale for text and neutral surfaces. */
const brandColorScheme = {
  1100: '#212626',
  1000: '#323a3a',
  900: '#0A2D31',
  800: '#1F464C',
  700: '#3C6068',
  600: '#4B6D76',
  500: '#5A7A84',
  400: '#7B95A0',
  300: '#9DB1BC',
  200: '#AFC0C9',
  100: '#C2CED6',
  50: '#D4DDE3',
  10: '#eaebef'
}

export const baseTheme = {
  config,
  breakpoints,
  styles: {
    global: (_props: GlobalProps) => ({
      html: {},
      body: {},
      '#__next': {},
      ':root': {}
    })
  },
  colors: {
    customColorScheme,
    primary: customColorScheme[600],
    secondary: '#ffab58',
    klarna: customColorScheme[500],
    background: {
      default: '#FFF',
      surface: '#f4f4f4',
      mildDark: 'rgb(1,50,75)',
      dark: 'rgb(0,25,38)',
      darkest: 'rgb(0,19,28)',
      translucent: 'rgba(244,244,244, 0.90)',
      overlayHalf: 'rgba(0,0,0, 0.25)',
      overlay: 'rgba(0,0,0, 0.5)',
      primary: 'rgba(146,176,156, .1)',
      secondary: 'rgba(205,15,50, .1)'
    },
    text: {
      default: brandColorScheme[1100],
      disabled: brandColorScheme[300],
      mild: brandColorScheme[500],
      light: 'white',
      darkGrey: brandColorScheme[1000],
      colored: '#315d91'
    },
    status: {
      success: 'rgba(29,195,149, 1)',
      warning: '#eed892',
      warning2: '#f5e6d6',
      error: '#f35f5f'
    },
    brand: {
      ...brandColorScheme,
      darkExtraLight: 'rgba(0,25,38,0.20)',
      darkGrey: '#222222'
    }
  },
  textStyles: {
    slogan: {
      fontFamily: 'var(--chakra-fonts-slogan)'
    }
  },
  textDecoration: {
    underline: {
      textDecorationLine: 'underline',
      textDecorationStyle: 'solid',
      textDecorationColor: 'color.highlight'
    }
  },
  shadows: {
    outline: '0 0 0 2px #cd0f32',
    primary: '0 0 0 2px #92b09c',
    secondary: '0 0 0 2px #cd0f32',
    primaryBlur: '0 0 4px 2px #92b09c',
    secondaryBlur: '0 0 4px 2px #cd0f32'
  },
  fonts
}
